import { ConstructGeneralViewStateParam, RestoreLocalSavedViewProps } from '@models/SavedViews';
import { useDispatch, useSelector } from 'react-redux';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { bindActionCreators } from 'redux';
import { actionCreators, actionCreatorsSideFilters } from '@state/Index';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { useRestoreSavedView } from '@ecosystem/newron-client-lib';
import {
  checkIsSubscribed,
  constructGeneralViewStateProps,
  getGrowthType,
  handleCustomPeriodicityAndRestoreGeneralViewState,
  handleRestoreMultibannerChange,
} from './SavedViewHookUtils';
import { useRestoreMultibannerRetailerClient } from './UseRestoreMultibannerRetailerClient';
import { useRestoreProductMultibannerRetailerClient } from './UseRestoreProductMultibannerRetailerClient';

export const useRestoreRetailerOverviewSavedView = (
  retailerOverviewRestoreLocalSavedViewProps: RestoreLocalSavedViewProps,
): void => {
  const {
    productType,
    setIsSubscribed,
    setSelectedCountries,
    setAppliedCountries,
    setFilters,
    setDefaultFilters,
    setGrowthOption,
    setRelativePeriod,
    setTimeFrame,
    setPeriod,
  } = retailerOverviewRestoreLocalSavedViewProps;

  // generate object holding the general states to be passed in the function later on
  const generalSavedViewProps = constructGeneralViewStateProps(retailerOverviewRestoreLocalSavedViewProps);
  const pageName = PageName.RETAILER_OVERVIEW;
  const pageType = PageType.RETAILER_OVERVIEW;

  const dispatch = useDispatch();
  const pagesConfig = usePageConfig();

  const mainFiltersState = useSelector((state: any) => state.mainFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);

  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);

  const { restoreMultibannerRetailerClient } = useRestoreMultibannerRetailerClient();
  const { restoreProductMultibannerRetailerClient } = useRestoreProductMultibannerRetailerClient();

  const applyRetailerOverviewViewState = (viewState): void => {
    if (!viewState) {
      return;
    }
    // initial checks
    if (!checkIsSubscribed(viewState.retailerClientState, pagesConfig, productType)) {
      setIsSubscribed(false);
      return;
    }

    const params: ConstructGeneralViewStateParam = {
      viewState,
      pagesConfig,
      pageType,
      pageName,
      updateSideFilters,
      updateMainFilters,
      mainFiltersState,
      generalSavedViewProps,
    };

    // restore the general states that exist in all pages
    handleCustomPeriodicityAndRestoreGeneralViewState(viewState, params);

    const { growthType } = viewState;

    if (viewState.mainFilter) {
      setRelativePeriod(viewState.mainFilter?.comparisonPeriod);
      setTimeFrame(viewState.mainFilter?.timeFrame);
      setPeriod(viewState.mainFilter?.period);
    }
    setGrowthOption(getGrowthType(growthType));

    handleRestoreMultibannerChange({
      viewState,
      retailerClientState,
      pageName,
      pageType,
      pagesConfig,
      restoreMultibannerRetailerClient,
      restoreProductMultibannerRetailerClient,
      setFilters,
      setAppliedCountries,
      setSelectedCountries,
      setDefaultFilters,
    });
  };
  useRestoreSavedView(applyRetailerOverviewViewState);
};
